/**
 * The Inventory Audit page's Help pop-up: common questions, the vendor-status
 * colors with each feed's raw values, and every problem type's meaning and
 * action. It replaced the always-on "Vendor status… What do these mean?"
 * strip above the worklist (Ian, 2026-09-28; mockup
 * https://claude.ai/artifact/Jn35xvQ4YA5PU5WZEQcua2).
 *
 * The explanations are LegendStrip's own pieces, not a copy, so the pop-up and
 * the parked sheet view's strip can never say different things. The status
 * translation is fetched when the pop-up opens — reference material nobody
 * needs on every page view, same as the strip's lazy expand.
 *
 * Modal chrome matches components/wholesale-auction/AuctionRowAlertModal.tsx
 * and components/photos/ReprocessConfirmModal.tsx (overlay, panel, header with
 * an X, Escape to close).
 */
import React, { useEffect, useRef, useState } from "react"
import { X } from "lucide-react"

import { getStatusLegend, StatusLegendResponse } from "../../api/audit"
import { THEME } from "../../config/theme"
import { useProblemTypes } from "../../hooks/useProblemTypes"
import { ProblemMeanings, StatusTranslation, VendorStatusWords } from "./LegendStrip"
import SeverityDot, { severityWord } from "./worklist/SeverityDot"

interface Props {
  onClose: () => void
}

/**
 * Written only from what the page already shows (Ian approved the draft in the
 * declutter mockup). Each answer states a fact of the product, so a change to
 * the behavior it describes should change the answer with it.
 */
const COMMON_QUESTIONS: Array<{ question: string; answer: string }> = [
  {
    question: "What do Critical, Warning and Cleanup mean?",
    answer:
      "Critical means a sale is being lost right now and the normal lag has run out — a car for " +
      "sale that shoppers cannot see, or a listing for a car the store no longer has. Warning means " +
      "the problem is inside its grace window and will turn critical on a clock if nobody acts. " +
      "Cleanup is hygiene with no sales impact. The green " +
      "Expected groups are cars where everything is as it should be; they're listed for " +
      "reference, not to be worked.",
  },
  {
    question: "What does NEW mean?",
    answer:
      "The car wasn't flagged for this problem in the previous run. NEW shows on Warning groups " +
      "only — a Critical row shows how long it has been open instead, because a critical is " +
      "past its grace window by definition. The Since last run box counts the new cars, and " +
      "each warning group shows how many of its cars are new.",
  },
  {
    question: "What does RESOLVED mean?",
    answer:
      "The car was flagged in the previous run and isn't now. That can mean someone fixed it, " +
      "the car sold, or a vendor feed caught up — on its own it doesn't prove a person fixed it. " +
      "The Tool Performance page counts only fixes someone marked as worked.",
  },
  {
    question: "What does Mark worked do?",
    answer:
      "It's the checkbox at the bottom of a car's detail panel. It records that you handled this " +
      "car for this problem. If the car is still flagged in the next run, its row shows STILL " +
      "FLAGGED with the date you worked it, so everyone can see it's waiting on a vendor, not on a person.",
  },
  {
    question: "I fixed a car. Why is it still on the list?",
    answer:
      "The vendor feeds take time to catch up — about a day is typical. Mark the car worked so " +
      "the list shows you already handled it.",
  },
  {
    question: "Which store am I looking at?",
    answer:
      "The tab under the page title. The page opens on your home store, and any other store — " +
      "or All stores — is one click away.",
  },
  {
    question: "What are the colored cells in the vendor status column?",
    answer:
      "Each cell is one vendor's view of the car, written as a word on a color. The next section " +
      "lists which of each vendor's statuses lands on which color.",
  },
]

const SECTIONS = [
  { key: "questions", title: "Common questions" },
  { key: "colors", title: "Vendor status colors" },
  { key: "types", title: "Every problem type" },
] as const

type SectionKey = (typeof SECTIONS)[number]["key"]

/** Same order the worklist stands its groups in. */
const SEVERITY_ORDER = ["critical", "warning", "cleanup", "expected"]

// Section-eyebrow styling after FileRequirementsPanel's "REQUIRED"/"OPTIONAL".
const eyebrowStyle: React.CSSProperties = {
  margin: `0 0 ${THEME.spacing.sm}`,
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

export default function AuditHelpModal({ onClose }: Props) {
  const registry = useProblemTypes()
  const [statusLegend, setStatusLegend] = useState<StatusLegendResponse | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const sectionRefs = useRef<Partial<Record<SectionKey, HTMLElement | null>>>({})

  // Esc-to-close — UX expectation for any modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  // Focus lands inside the dialog, so keyboard and screen-reader users start
  // where the content is rather than on the page behind it.
  useEffect(() => {
    closeButtonRef.current?.focus()
  }, [])

  // The status translation loads when the pop-up opens. A failure leaves it in
  // its loading line; the questions and problem meanings still render.
  useEffect(() => {
    let cancelled = false
    getStatusLegend()
      .then((legend) => {
        if (!cancelled) setStatusLegend(legend)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  const problemTypes = registry === null ? [] : Object.values(registry)

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: THEME.modal.overlay,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: THEME.spacing.md,
        // Above the row drawer (1000), which can be open when Help is clicked.
        zIndex: 1100,
        fontFamily: THEME.fonts.primary,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-help-title"
        style={{
          background: THEME.colors.neutral.light.surface,
          borderRadius: THEME.borderRadius.md,
          boxShadow: THEME.modal.shadow,
          width: "min(720px, 100%)",
          maxHeight: "86vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: THEME.spacing.md,
            padding: `${THEME.spacing.md} ${THEME.spacing.lg} ${THEME.spacing.sm}`,
          }}
        >
          <div
            id="audit-help-title"
            style={{ fontSize: 16, fontWeight: 600, color: THEME.colors.neutral.light.textPrimary }}
          >
            How to read the Inventory Audit
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "none",
              border: "none",
              color: THEME.colors.neutral.light.textSecondary,
              cursor: "pointer",
              padding: 4,
            }}
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <nav
          aria-label="Help sections"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            padding: `0 ${THEME.spacing.lg} ${THEME.spacing.s12}`,
            borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
          }}
        >
          {SECTIONS.map(({ key, title }) => (
            <button
              key={key}
              type="button"
              // scrollIntoView is optional-called: jsdom (the test runner) has none.
              onClick={() => sectionRefs.current[key]?.scrollIntoView?.({ behavior: "smooth", block: "start" })}
              style={{
                fontSize: 12,
                fontFamily: THEME.fonts.primary,
                color: THEME.colors.neutral.light.textPrimary,
                background: THEME.problemDisplay.legendBackground,
                border: `1px solid ${THEME.colors.neutral.light.border}`,
                borderRadius: THEME.borderRadius.pill,
                padding: "3px 10px",
                cursor: "pointer",
              }}
            >
              {title}
            </button>
          ))}
        </nav>

        <div
          style={{
            overflowY: "auto",
            padding: `${THEME.spacing.md} ${THEME.spacing.lg} ${THEME.spacing.lg}`,
            fontSize: 13,
            lineHeight: 1.5,
            color: THEME.colors.neutral.light.textSecondary,
            display: "flex",
            flexDirection: "column",
            gap: THEME.spacing.lg,
          }}
        >
          <section ref={(node) => { sectionRefs.current.questions = node }}>
            <h3 style={eyebrowStyle}>Common questions</h3>
            {COMMON_QUESTIONS.map(({ question, answer }) => (
              <div
                key={question}
                style={{ borderTop: `1px solid ${THEME.colors.neutral.light.border}`, padding: "10px 0" }}
              >
                <div style={{ fontSize: 13.5, fontWeight: 600, color: THEME.colors.neutral.light.textPrimary }}>
                  {question}
                </div>
                <div>{answer}</div>
              </div>
            ))}
          </section>

          <section ref={(node) => { sectionRefs.current.colors = node }}>
            <h3 style={eyebrowStyle}>Vendor status colors</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: THEME.spacing.s12 }}>
              <VendorStatusWords />
            </div>
            <StatusTranslation legend={statusLegend} />
          </section>

          <section ref={(node) => { sectionRefs.current.types = node }}>
            <h3 style={eyebrowStyle}>Every problem type</h3>
            {registry === null ? (
              <span>Loading problem meanings…</span>
            ) : (
              SEVERITY_ORDER.map((severity) => {
                const types = problemTypes.filter((info) => info.severity === severity)
                if (types.length === 0) return null
                return (
                  <div key={severity} style={{ marginBottom: THEME.spacing.s12 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: THEME.spacing.sm,
                        fontWeight: 600,
                        color: THEME.colors.neutral.light.textPrimary,
                        margin: `${THEME.spacing.sm} 0 ${THEME.spacing.xs}`,
                      }}
                    >
                      <SeverityDot severity={severity} decorative />
                      {severityWord(severity)}
                    </div>
                    <ProblemMeanings types={types} stacked />
                  </div>
                )
              })
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
